import { titleIdentityMarkup } from "../js/fishydex/title-presentation.js";
import { queryMeaningCopy } from "../js/query/presentation.js";
import { buildAppliedSearchTermsView } from "../js/components/applied-search-terms.js";
import { contestQueryTermView } from "../js/fishing-contest/query-presentation.js";
import { itemGroupAppliedMarkup } from "../js/query/item-group-presentation.js";
import {
  serializeSearchExpressionDsl,
  tradeNpcKeyFromSearchTerm,
} from "./map-search-contract.js";
import { mapText, siteText } from "./map-i18n.js";
import {
  buildItemGroupPresentation,
} from "./map-query-presentation.js";
import {
  specialItemIdentity,
  specialItemReferenceForTerm,
  specialItemReferencesForCatalogItems,
} from "./map-special-item-references.js";
import {
  buildSearchExpressionSourceBindings,
  searchExpressionOperatorBindingKey,
} from "./search-expression/source-bindings.js";

function normalizeExpressionOperator(value) {
  return String(value ?? "").trim().toLowerCase() === "and" ? "and" : "or";
}


function patchBoundLabel(bound) {
  return String(bound || "").trim().toLowerCase() === "to"
    ? mapText("search.bound.before")
    : mapText("search.bound.after");
}

function normalizeSourceKind(sourceKind) {
  return String(sourceKind || "").trim().toLowerCase().replace(/-/g, "_");
}

function sourceKindLabel(sourceKind) {
  const normalized = normalizeSourceKind(sourceKind);
  switch (normalized) {
    case "rod":
      return siteText("domain.method.rod");
    case "zone":
      return siteText("domain.source.zone");
    case "hotspot":
      return siteText("domain.source.hotspot");
    case "harpoon":
      return siteText("domain.method.harpoon");
    case "ice_fishing":
      return siteText("domain.source.ice_fishing");
    default:
      return normalized ? String(sourceKind || "").trim() : "";
  }
}

function sourceKindsDescription(sourceKinds) {
  const labels = [];
  for (const sourceKind of Array.isArray(sourceKinds) ? sourceKinds : []) {
    const label = sourceKindLabel(sourceKind);
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  }
  return labels.join(", ");
}

function specialDisplayIdentity(reference, fallback = {}) {
  const identity = specialItemIdentity(reference, fallback);
  if (!identity.iconImage && !identity.icon_image) {
    delete identity.iconImage;
    delete identity.icon_image;
  }
  return identity;
}

function layerFilterLayerLabel(layerId) {
  const normalized = String(layerId ?? "").trim().toLowerCase();
  if (normalized === "zones" || normalized === "zone_mask") {
    return mapText("layers.search_kind.zones");
  }
  if (normalized === "hotspots") {
    return siteText("domain.source.hotspot");
  }
  if (normalized === "bookmarks") {
    return mapText("toolbar.bookmarks");
  }
  return normalized
    ? normalized.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : mapText("search.kind.layer");
}

function fishIdentityWithCatalogItem(fish, fishId, context) {
  const itemId = Number.parseInt(fish?.itemId ?? fish?.item_id ?? fishId, 10);
  const item = Number.isInteger(itemId) ? context.itemLookup.get(itemId) : null;
  return {
    ...(item || {}),
    ...(fish || {}),
    fishId,
    itemId: Number.isInteger(itemId) ? itemId : item?.itemId,
    iconItemId: fish?.iconItemId ?? item?.iconItemId,
    iconImage: fish?.iconImage || item?.iconImage,
  };
}

function cueContentMarkup(label, context, leadingMarkup = "") {
  return `
    <span class="fishy-applied-cue-content">
      ${leadingMarkup}
      <span class="fishy-applied-cue-label">${context.escapeHtml(label)}</span>
    </span>
  `;
}

function iconCueMarkup(label, context, icon = "filter-2-fill") {
  return cueContentMarkup(
    label,
    context,
    `<svg class="fishy-icon size-4 shrink-0 text-base-content/60" viewBox="0 0 24 24" aria-hidden="true">
      <use width="100%" height="100%" href="#fishy-${context.escapeHtml(icon)}"></use>
    </svg>`,
  );
}

function buildAppliedSearchTermNode(term, context, path, options = {}) {
  if (!term || typeof term !== "object") {
    return null;
  }
  const negated = options.negated === true;

  if (term.kind === "fishing-contest") {
    return { ...contestQueryTermView(context.contestFish, siteText), path, negated };
  }

  if (term.kind === "status" || term.kind === "grade") {
    const filterTerm = term.kind === "status"
      ? term.status
      : term.grade;
    const metadata = context.fishFilterTermMetadata[filterTerm];
    const label = metadata?.label || filterTerm;
    return {
      type: "term",
      key: `${term.kind}:${filterTerm}`,
      path,
      label,
      kindLabel: term.kind === "grade"
        ? siteText("domain.taxonomy.grade")
        : term.kind === "status"
          ? mapText("search.kind.status")
          : mapText("search.kind.filter"),
      grade: filterTerm,
      negated,
      contentMarkup: cueContentMarkup(
        label,
        context,
        context.fishFilterTermIconMarkup(filterTerm),
      ),
    };
  }

  if (term.kind === "patch-bound") {
    const patchId = String(term.patchId || "").trim();
    const patch = patchId ? context.patchLookup.get(patchId) || null : null;
    const patchLabel = patch?.label || patchId;
    const boundLabel = patchBoundLabel(term.bound);
    return {
      type: "term",
      key: `patch-bound:${term.bound}:${patchId || "__pending__"}`,
      path,
      label: patchLabel ? `${boundLabel} ${patchLabel}` : boundLabel,
      kindLabel: mapText("search.kind.date"),
      grade: "patch",
      negated: false,
      description: "",
      contentMarkup: iconCueMarkup(
        patchLabel ? `${boundLabel} ${patchLabel}` : boundLabel,
        context,
        "calendar-2-fill",
      ),
    };
  }

  if (term.kind === "title") {
    const title = context.titles?.find(title => title.titleId === term.titleId);
    const name = title?.name || String(term.titleId);
    return { type: "term", key: `title:${term.titleId}`, path, label: name,
      kindLabel: siteText("fishydex.titles.title_column"), negated,
      contentMarkup: title ? titleIdentityMarkup(title) : iconCueMarkup(name, context, "medal-fill") };
  }
  if (term.kind === "fish") {
    const fish = context.fishLookup.get(term.fishId);
    const name = fish?.name || siteText("domain.entity.fish_id", { id: term.fishId });
    const identity = {
      ...fishIdentityWithCatalogItem(fish, term.fishId, context),
      name,
    };
    return {
      type: "term",
      key: `fish:${term.fishId}`,
      path,
      label: name,
      kindLabel: siteText("domain.entity.fish"),
      grade: context.resolveFishGrade(fish),
      negated,
      contentMarkup:
        context.fishIdentityMarkup(identity, { interactive: true })
        || `<span>${context.escapeHtml(name)}</span>`,
    };
  }

  if (term.kind === "item") {
    const item = context.itemLookup.get(term.itemId);
    const special = specialItemReferenceForTerm(term, context.specialItemReferences);
    const displayItem = special
      ? specialDisplayIdentity(special, item || { itemId: term.itemId })
      : item;
    const name = displayItem?.name || siteText("domain.entity.item_id", { id: term.itemId });
    const sourceKinds = term.sourceKinds?.length ? term.sourceKinds : displayItem?.sourceKinds;
    const identityMarkup = displayItem
      ? context.fishIdentityMarkup({ ...displayItem, name }, { interactive: true })
      : "";
    return {
      type: "term",
      key: `item:${term.itemId}`,
      path,
      label: name,
      description: sourceKindsDescription(sourceKinds),
      grade: context.resolveFishGrade(displayItem),
      negated,
      contentMarkup: identityMarkup || `<span>${context.escapeHtml(name)}</span>`,
    };
  }

  if (term.kind === "item-group") {
    const itemIds = Array.isArray(term.itemIds) ? term.itemIds : [];
    const items = itemIds.map((itemId) => context.itemLookup.get(itemId)).filter(Boolean);
    const special = specialItemReferenceForTerm(term, context.specialItemReferences);
    const presentation = context.buildItemGroupPresentation(
      term,
      context.itemLookup,
      special,
    );
    const name = presentation.label;
    const sourceKinds = term.sourceKinds?.length
      ? term.sourceKinds
      : Array.from(new Set(items.flatMap((item) => item.sourceKinds || []))).sort();
    const appliedMarkup = context.itemGroupAppliedMarkup(
      presentation,
      context.itemLookup,
    );
    const memberDescription = presentation.directMemberNames.join(", ");
    const sourceDescription = sourceKindsDescription(sourceKinds);
    return {
      type: "term",
      key: `item-group:${itemIds.join(",")}`,
      path,
      label: name,
      description: [memberDescription, sourceDescription].filter(Boolean).join(" · "),
      grade: presentation.grade,
      negated,
      reserveSecondaryRow: presentation.isSpecial,
      contentMarkup: appliedMarkup || `<span>${context.escapeHtml(name)}</span>`,
    };
  }

  if (term.kind === "zone") {
    const zone = context.zoneLookup.get(term.zoneRgb);
    const name = zone?.name || mapText("search.zone.fallback", { zone: context.formatZone(term.zoneRgb) });
    return {
      type: "term",
      key: `zone:${term.zoneRgb}`,
      path,
      label: name,
      grade: "zone",
      negated,
      description: "",
      contentMarkup:
        context.zoneIdentityMarkup(
          {
            zoneRgb: term.zoneRgb,
            name,
            r: zone?.r,
            g: zone?.g,
            b: zone?.b,
          },
          { interactive: true },
        ) || `<span>${context.escapeHtml(name)}</span>`,
    };
  }

  if (term.kind === "semantic") {
    const semanticTerm =
      context.semanticLookup.get(
        `${String(term.layerId || "").trim()}:${Number.parseInt(term.fieldId, 10)}`,
      ) || null;
    const name = semanticTerm?.label || mapText("search.semantic.field", { id: term.fieldId });
    return {
      type: "term",
      key: `semantic:${term.layerId}:${term.fieldId}`,
      path,
      label: name,
      kindLabel: semanticTerm?.layerName || siteText("domain.entity.map"),
      grade: "semantic",
      negated,
      description: semanticTerm?.description || "",
      contentMarkup:
        context.semanticIdentityMarkup(name, { interactive: true })
        || `<span>${context.escapeHtml(name)}</span>`,
    };
  }

  if (term.kind === "layer-filter") {
    const expressionText = serializeSearchExpressionDsl({ type: "term", term });
    const label = expressionText || `filter:${term.layerId || ""}`;
    const layerLabel = layerFilterLayerLabel(term.layerId);
    const cueLabel = mapText("search.layer_filter.label", { layer: layerLabel });
    return {
      type: "term",
      key: `layer-filter:${label}`,
      path,
      label: cueLabel,
      kindLabel: mapText("search.kind.layer_filter"),
      grade: "semantic",
      negated,
      description: label,
      contentMarkup: iconCueMarkup(cueLabel, context),
    };
  }

  if (term.kind === "layer-fact") {
    const npcKey = tradeNpcKeyFromSearchTerm(term);
    if (npcKey != null) {
      const npc = context.tradeNpcLookup.get(npcKey);
      const name = npc?.npcName || `NPC ${npcKey}`;
      return {
        type: "term",
        key: `trade-npc:${npcKey}`,
        path,
        label: name,
        kindLabel: "Trade NPC",
        grade: "semantic",
        negated,
        description: npc?.assignedRegionLabel || npc?.sellOriginLabel || "",
        contentMarkup:
          context.tradeNpcIdentityMarkup({ ...(npc || {}), npcKey, npcName: name })
          || `<span>${context.escapeHtml(name)}</span>`,
      };
    }
    const expressionText = serializeSearchExpressionDsl({ type: "term", term });
    const label = expressionText || `fact:${term.layerId || ""}:${term.factId || ""}`;
    const cueLabel = `${layerFilterLayerLabel(term.layerId)} · ${term.factId || "fact"}`;
    return {
      type: "term",
      key: `layer-fact:${label}`,
      path,
      label,
      kindLabel: mapText("search.kind.layer_fact"),
      grade: "semantic",
      negated,
      description: label,
      contentMarkup: iconCueMarkup(cueLabel, context),
    };
  }

  return null;
}

function buildAppliedSearchGroupCue(expression, context) {
  const terms = (Array.isArray(expression?.children) ? expression.children : [])
    .map((child) => child?.type === "term" ? child.term : null);
  if (
    normalizeExpressionOperator(expression?.operator) !== "and" ||
    terms.length !== 2 ||
    terms.some((term) => term?.kind !== "patch-bound")
  ) {
    return {};
  }
  const patchIds = new Set(terms.map((term) => String(term.patchId || "").trim()));
  const bounds = new Set(terms.map((term) => String(term.bound || "").trim().toLowerCase()));
  if (patchIds.size !== 1 || bounds.size !== 2 || !bounds.has("from") || !bounds.has("to")) {
    return {};
  }
  const patchId = [...patchIds][0];
  const patch = patchId ? context.patchLookup.get(patchId) || null : null;
  const label = String(patch?.label || patchId || mapText("search.patch.choose_date")).trim();
  return {
    label,
    kindLabel: mapText("search.kind.date"),
    description: patchId,
    grade: "patch",
    contentMarkup: iconCueMarkup(label, context, "calendar-2-fill"),
  };
}

function buildAppliedSearchExpressionNode(expression, context, path = "root") {
  if (!expression || typeof expression !== "object") {
    return null;
  }

  if (String(expression.type || "").trim().toLowerCase() === "term") {
    const termNode = buildAppliedSearchTermNode(expression.term, context, path, {
      negated: expression.negated === true,
    });
    const sourceBinding = context.sourceBindings.get(path);
    return termNode && sourceBinding ? { ...termNode, ...sourceBinding } : termNode;
  }

  if (String(expression.type || "").trim().toLowerCase() === "container") {
    const child = buildAppliedSearchExpressionNode(
      expression.expression,
      context,
      `${path}.0`,
    );
    return child
      ? {
          type: "container",
          key: path,
          path,
          negated: expression.negated === true,
          children: [child],
        }
      : null;
  }

  const children = (Array.isArray(expression.children) ? expression.children : [])
    .map((child, index) => buildAppliedSearchExpressionNode(child, context, `${path}.${index}`))
    .filter(Boolean);

  const groupNode = {
    type: "group",
    key: path,
    path,
    ...buildAppliedSearchGroupCue(expression, context),
    operator: normalizeExpressionOperator(expression.operator),
    negated: expression.negated === true,
    operatorSources: children.slice(1).map((_child, index) =>
      context.sourceBindings.get(searchExpressionOperatorBindingKey(path, index + 1)) || null
    ),
    children,
  };
  const sourceBinding = context.sourceBindings.get(path);
  return sourceBinding ? { ...groupNode, ...sourceBinding } : groupNode;
}

function normalizeExpressionIssues(stateBundle) {
  return Array.isArray(stateBundle?.inputState?.search?.expressionIssues)
    ? stateBundle.inputState.search.expressionIssues.filter((issue) => issue && typeof issue === "object")
    : [];
}

function expressionIssueSeverity(issue) {
  return issue?.severity === "warning" ? "warning" : "error";
}

function expressionIssueMessage(issue) {
  const message = String(issue?.message || "").trim();
  if (message) {
    return message;
  }
  const code = String(issue?.code || "").trim();
  return code || "Search expression issue";
}

function buildSearchExpressionIssuesView(stateBundle, escapeHtml) {
  const issues = normalizeExpressionIssues(stateBundle);
  if (!issues.length) {
    return { hasContent: false, html: "", renderKey: "[]" };
  }
  const renderKey = JSON.stringify(
    issues.map((issue) => [
      issue.stage || "",
      expressionIssueSeverity(issue),
      issue.code || "",
      issue.span?.start ?? 0,
      issue.span?.end ?? 0,
      issue.raw || "",
      issue.message || "",
    ]),
  );
  const html = `
    <div class="fishymap-search-expression-issues flex flex-col gap-2">
      ${issues.map((issue) => {
        const severity = expressionIssueSeverity(issue);
        const severityLabel = siteText(`health.issue.severity.${severity}`);
        const alertClass = severity === "warning" ? "alert-warning" : "alert-error";
        const raw = String(issue.raw || "").trim();
        return `
          <div class="alert ${alertClass} alert-soft items-start gap-2 rounded-md px-3 py-2 text-xs" role="${severity === "warning" ? "status" : "alert"}">
            <span class="font-semibold">${escapeHtml(severityLabel)}</span>
            <span class="min-w-0 flex-1">${escapeHtml(expressionIssueMessage(issue))}</span>
            ${raw ? `<code class="rounded bg-base-100/70 px-1.5 py-0.5 text-[0.6875rem]">${escapeHtml(raw)}</code>` : ""}
          </div>
        `;
      }).join("")}
    </div>
  `;
  return { hasContent: true, html, renderKey };
}

export function renderSearchSelection(elements, stateBundle, fishLookup, options = {}) {
  const resolveSearchExpression =
    typeof options.resolveSearchExpression === "function"
      ? options.resolveSearchExpression
      : (bundle) => bundle?.inputState?.search?.expression ?? null;
  const buildSemanticTermLookup =
    typeof options.buildSemanticTermLookup === "function"
      ? options.buildSemanticTermLookup
      : () => new Map();
  const escapeHtml = typeof options.escapeHtml === "function" ? options.escapeHtml : (value) => String(value || "");
  const fishFilterTermIconMarkup =
    typeof options.fishFilterTermIconMarkup === "function"
      ? options.fishFilterTermIconMarkup
      : () => "";
  const fishIdentityMarkup =
    typeof options.fishIdentityMarkup === "function" ? options.fishIdentityMarkup : () => "";
  const buildGroupPresentation =
    typeof options.buildItemGroupPresentation === "function"
      ? options.buildItemGroupPresentation
      : buildItemGroupPresentation;
  const groupAppliedMarkup =
    typeof options.itemGroupAppliedMarkup === "function"
      ? options.itemGroupAppliedMarkup
      : itemGroupAppliedMarkup;
  const zoneIdentityMarkup =
    typeof options.zoneIdentityMarkup === "function" ? options.zoneIdentityMarkup : () => "";
  const semanticIdentityMarkup =
    typeof options.semanticIdentityMarkup === "function"
      ? options.semanticIdentityMarkup
      : () => "";
  const tradeNpcIdentityMarkup =
    typeof options.tradeNpcIdentityMarkup === "function"
      ? options.tradeNpcIdentityMarkup
      : () => "";
  const resolveFishGrade =
    typeof options.resolveFishGrade === "function" ? options.resolveFishGrade : () => "unknown";
  const formatZone = typeof options.formatZone === "function" ? options.formatZone : (value) => String(value || "");
  const fishFilterTermMetadata = options.fishFilterTermMetadata || {};
  const itemLookup = options.itemLookup instanceof Map ? options.itemLookup : new Map();
  const catalogItems = stateBundle?.state?.catalog?.items;
  const specialItemReferences = specialItemReferencesForCatalogItems(
    Array.isArray(catalogItems) && catalogItems.length
      ? catalogItems
      : Array.from(itemLookup.values()),
  );
  const zoneLookup = new Map((elements.zoneCatalog || []).map((zone) => [zone.zoneRgb, zone]));
  const patchLookup = new Map(
    (stateBundle?.state?.catalog?.patches || []).map((patch) => [String(patch.patchId || "").trim(), patch]),
  );
  const semanticLookup = buildSemanticTermLookup(stateBundle);
  const tradeNpcLookup = options.tradeNpcLookup instanceof Map
    ? options.tradeNpcLookup
    : new Map();
  const expression = resolveSearchExpression(stateBundle);
  const sourceText = String(
    options.sourceText ?? stateBundle?.inputState?.search?.rawExpression ?? "",
  );
  const sourceBindings = buildSearchExpressionSourceBindings(
    sourceText,
    options.rawExpressionTree ?? stateBundle?.inputState?.search?.rawExpressionTree,
    expression,
  );
  const expressionView = buildAppliedSearchExpressionNode(
    expression,
    {
      escapeHtml,
      fishFilterTermIconMarkup,
      fishFilterTermMetadata,
      fishIdentityMarkup,
      buildItemGroupPresentation: buildGroupPresentation,
      itemGroupAppliedMarkup: groupAppliedMarkup,
      contestFish: options.contestFish,
      titles: options.titles,
      fishLookup,
      itemLookup,
      formatZone,
      patchLookup,
      resolveFishGrade,
      semanticIdentityMarkup,
      semanticLookup,
      specialItemReferences,
      sourceBindings,
      tradeNpcIdentityMarkup,
      tradeNpcLookup,
      zoneIdentityMarkup,
      zoneLookup,
    },
    "root",
  );

  const { hasContent: hasAnySelection, html, renderKey } = buildAppliedSearchTermsView(expressionView, {
    escapeHtml,
    meaningCopy: queryMeaningCopy(siteText),
    queryTextRelation: options.queryTextRelation,
    sourceText,
  });
  const issuesView = buildSearchExpressionIssuesView(stateBundle, escapeHtml);
  const hasAnyContent = hasAnySelection || issuesView.hasContent;
  const combinedRenderKey = JSON.stringify({
    issues: issuesView.renderKey,
    selection: renderKey,
  });
  const combinedHtml = [
    issuesView.html,
    hasAnySelection ? html : "",
  ].filter(Boolean).join("");

  if (elements.searchSelection.dataset.renderKey === combinedRenderKey) {
    elements.searchSelection.hidden = !hasAnyContent;
    if (elements.searchSelectionShell) {
      elements.searchSelectionShell.hidden = !hasAnyContent;
    }
    if (elements.searchWindow) {
      elements.searchWindow.dataset.hasSelection = hasAnyContent ? "true" : "false";
    }
    return;
  }
  elements.searchSelection.dataset.renderKey = combinedRenderKey;

  if (!hasAnyContent) {
    elements.searchSelection.innerHTML = "";
    elements.searchSelection.hidden = true;
    if (elements.searchSelectionShell) {
      elements.searchSelectionShell.hidden = true;
    }
    if (elements.searchWindow) {
      elements.searchWindow.dataset.hasSelection = "false";
    }
    return;
  }

  elements.searchSelection.hidden = false;
  if (elements.searchSelectionShell) {
    elements.searchSelectionShell.hidden = false;
  }
  if (elements.searchWindow) {
    elements.searchWindow.dataset.hasSelection = "true";
  }

  elements.searchSelection.innerHTML = combinedHtml;
}
